import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Banknote, Download, FileText, LandPlot, Leaf } from 'lucide-react';
import { PageShell, Stack } from '@/ui/layout';
import { Card, Chip, Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/primitives';
import { useRelatoriosGestao } from '@/modules/gestao-relatorios/api';
import { splitAreaReports } from '@/modules/gestao-relatorios/grouping';
import type { AreaRelatorio, RelatorioGestao } from '@/modules/gestao-relatorios/schemas';

const AREAS: Array<{
  id: AreaRelatorio;
  title: string;
  agent: string;
  icon: typeof Banknote;
}> = [
  { id: 'credito', title: 'Crédito Rural', agent: 'Davi', icon: Banknote },
  { id: 'fundiario', title: 'Regularização Fundiária', agent: 'Rebeca', icon: LandPlot },
  { id: 'ambiental', title: 'Regularização Ambiental', agent: 'Flora', icon: Leaf },
];

function formatDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat('pt-BR', {
        timeZone: 'America/Porto_Velho',
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(date);
}

function ReportRow({ report }: { report: RelatorioGestao }) {
  return (
    <div className="flex items-center gap-3 border-b border-border py-3 last:border-b-0">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface2 text-text-muted">
        <FileText size={17} strokeWidth={1.6} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-text">{report.titulo}</p>
        <p className="text-xs text-text-muted">
          {formatDate(report.data_base)} · IA {report.agente}
        </p>
      </div>
      {report.resumo_semanal ? <Chip tone="warn" size="sm">Semanal</Chip> : null}
      <a
        href={report.download_url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-semibold text-text transition-colors hover:bg-surface2"
      >
        <Download size={14} />
        Abrir
      </a>
    </div>
  );
}

function AreaCard({
  area,
  reports,
  emptyMessage,
}: {
  area: (typeof AREAS)[number];
  reports: RelatorioGestao[];
  emptyMessage: string;
}) {
  const Icon = area.icon;
  return (
    <Card padding="md" className="min-w-0">
      <div className="mb-3 flex items-center gap-3 border-b border-border pb-3">
        <span className="flex size-10 items-center justify-center rounded-xl bg-[var(--nav-active-bg)] text-[var(--nav-active-fg)]">
          <Icon size={19} strokeWidth={1.6} />
        </span>
        <div>
          <h2 className="text-sm font-semibold text-text">{area.title}</h2>
          <p className="text-xs text-text-muted">IA {area.agent}</p>
        </div>
        <Chip tone="neutral" size="sm" className="ml-auto">{reports.length}</Chip>
      </div>
      {reports.length === 0 ? (
        <p className="py-7 text-center text-sm text-text-muted">{emptyMessage}</p>
      ) : (
        reports.map((report) => <ReportRow key={report.id} report={report} />)
      )}
    </Card>
  );
}

export const Route = createFileRoute('/_authed/gestao')({
  component: GestaoPage,
});

function GestaoPage() {
  const { data = [], isLoading, error } = useRelatoriosGestao();
  const [tab, setTab] = useState('atuais');
  const grouped = AREAS.map((area) => ({ area, ...splitAreaReports(data, area.id) }));
  const historyCount = grouped.reduce((total, item) => total + item.history.length, 0);

  return (
    <PageShell
      title="Gestão"
      titleSize="sm"
      headerClassName="!pt-4"
    >
      <Stack className="gap-5">
        <p className="text-sm text-text-muted">Relatórios consolidados das áreas de negócio. Em Crédito Rural, a visão principal separa o último relatório de cards do último relatório de liberações programadas.</p>
        {isLoading ? (
          <Card padding="lg"><p className="text-sm text-text-muted">Carregando relatórios…</p></Card>
        ) : error ? (
          <Card padding="lg"><p className="text-sm text-danger">Não foi possível carregar os relatórios gerenciais.</p></Card>
        ) : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="atuais" count={grouped.reduce((total, item) => total + item.latest.length, 0)}>Últimos relatórios</TabsTrigger>
              <TabsTrigger value="historico" count={historyCount}>Relatórios anteriores</TabsTrigger>
            </TabsList>
            <TabsContent value="atuais">
              <div className="grid gap-4 xl:grid-cols-3">
                {grouped.map(({ area, latest }) => (
                  <AreaCard key={area.id} area={area} reports={latest} emptyMessage="Nenhum relatório publicado." />
                ))}
              </div>
            </TabsContent>
            <TabsContent value="historico">
              <div className="grid gap-4 xl:grid-cols-3">
                {grouped.map(({ area, history }) => (
                  <AreaCard key={area.id} area={area} reports={history} emptyMessage="Nenhum relatório anterior." />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </Stack>
    </PageShell>
  );
}
